<template>
	<div class="field">
		<neko-field v-bind="{ ...$props, ...$attrs }" :error="error" :for="id" v-on="$listeners">
			<template v-if="label && !helpDisabled" #label>
				<span
					class="icon is-clickable has-tooltip-multi-line has-tooltip-top has-tooltip-right"
					v-tooltip.top-start="helpTooltip"
				>
					<span class="fa fa-question-circle"></span>
				</span>
				{{ label }}
			</template>
			<template>
				<div class="formula-editor-wrapper" ref="editorWrapper">
					<div
						class="formula-editor"
						:style="{ resize }"
						ref="editorContainer"
						@contextmenu.prevent="openContextMenu"
					></div>
					<div
						v-if="isContextMenuVisible"
						ref="contextMenu"
						class="formula-editor-context-menu"
						tabindex="-1"
						@keydown.esc.prevent="closeContextMenu"
					>
						<button
							type="button"
							class="formula-editor-context-menu__item"
							:class="{ 'formula-editor-context-menu__item--active': lastUsedFormatMode === 'compact' }"
							:disabled="disabled || sending"
							@click="formatCompactFromContextMenu"
						>
							{{ $t("contextMenu.formatCompact") }}
							<span
								v-if="lastUsedFormatMode === 'compact'"
								class="formula-editor-context-menu__shortcut"
							>
								{{ contextMenuFormatShortcut }}
							</span>
						</button>
						<button
							type="button"
							class="formula-editor-context-menu__item"
							:class="{ 'formula-editor-context-menu__item--active': lastUsedFormatMode === 'readable' }"
							:disabled="disabled || sending"
							@click="formatReadableFromContextMenu"
						>
							{{ $t("contextMenu.formatReadable") }}
							<span
								v-if="lastUsedFormatMode === 'readable'"
								class="formula-editor-context-menu__shortcut"
							>
								{{ contextMenuFormatShortcut }}
							</span>
						</button>
					</div>
				</div>
			</template>

			<slot v-for="(slot, name) in $slots" :name="name" :slot="name"></slot>

			<template #help>
				<slot name="help"></slot>
			</template>
		</neko-field>
	</div>
</template>

<style lang="sass" scoped>
.formula-editor-wrapper
	position: relative

.formula-editor
	box-sizing: border-box
	border-radius: 4px
	border: 1px solid #dbdbdb
	font-size: 0.9rem
	min-height: 35px
	overflow: hidden

.formula-editor-context-menu
	background: #ffffff
	border: 1px solid #d1d5db
	border-radius: 6px
	box-shadow: 0 10px 25px rgba(15, 23, 42, 0.16)
	min-width: 220px
	padding: 4px
	position: fixed
	z-index: 1001

.formula-editor-context-menu__item
	align-items: center
	background: transparent
	border: 0
	border-radius: 4px
	color: #111827
	cursor: pointer
	display: flex
	font-size: 0.875rem
	justify-content: space-between
	padding: 8px 10px
	width: 100%

	&:hover:not(:disabled)
		background: #f3f4f6

	&:disabled
		color: #9ca3af
		cursor: default

	&--active
		font-weight: 600

.formula-editor-context-menu__shortcut
	color: #6b7280
	margin-left: 12px
</style>

<script lang="ts">
import { createPopper, type Instance, type VirtualElement } from "@popperjs/core";
import axios, { CancelToken, type CancelTokenSource } from "axios";

import { ExternalTableQueryDto } from "@/lib/apis/hub/models";
import AdditionalDataApi from "@/lib/apis/hub/operations/AdditionalDataResourceApi";
import EmployeesDetailsApi from "@/lib/apis/hub/operations/EmployeesDetailsResourceApi";
import ExternalTableQueryApi from "@/lib/apis/hub/operations/ExternalTableQueryResourceApi";
import InstitutionDetailsApi from "@/lib/apis/hub/operations/InstitutionsDetailsResourceApi";
import ReferentialPayApi from "@/lib/apis/hub/operations/ReferentialPayResourceApi";

import { HtmlInputForm } from "@emasofts/common-vuejs-form";
import { Component, Prop, Ref, Watch } from "vue-property-decorator";

import NekoField from "../Field.vue";
import { extractVariable, resolveCompletionContext } from "./formulaCompletionContext";
import { collectSyntaxErrors, formatFormula } from "./formulaEditor.antlr";
import { createFormulaCodeMirrorEditor, type FormulaCodeMirrorEditor } from "./formulaEditor.codemirror";
import { FORMULA_IDENTIFIER_REGEX_SOURCE, VARIABLE_PARTS_SEPARATOR } from "./formulaEditor.constants";
import { getFunctionArgumentSuggestions } from "./formulaFunctions";
import { quote, unquote } from "./formulaEditor.table";
import {
	CompletionContext,
	ExternalTableExpectedArg,
	FormulaCompletionOption,
	FormulaFormatMode,
	FormulaRange,
	FormulaWord,
	FunctionCompletionContext,
	Suggestion,
	TableCompletionContext,
	Variable,
	VariableCompletionContext,
	VariableType
} from "./formulaEditor.types";
import {
	getExpectedTableArg,
	getPrefixSuggestions,
	getLigneColumnSuggestions,
	getVariableSuggestionsRequest,
	toVariableSuggestions
} from "./formulaSuggestions";

@Component({
	components: {
		NekoField
	}
})
export default class FormulaEditor extends HtmlInputForm {
	private static readonly WORD_END_REGEX = new RegExp(`${FORMULA_IDENTIFIER_REGEX_SOURCE}$`, "u");
	private static readonly COMPLETION_SECTION_RANKS: Record<string, number> = {
		"Fonctions": 0,
		"Préfixes de variable": 1,
		"Variables": 2,
		"Colonnes de ligne": 3,
		"Tables externes": 4,
		"Valeurs de fonction": 5,
		"Argument attendu": 6,
		"Valeurs attendues": 7
	};

	public declare readonly disabled: boolean;
	public declare readonly error: string;
	public declare readonly sending: boolean;
	public declare readonly value: string;
	public declare readonly placeholder: string;
	public declare readonly label: string;

	@Prop({ default: "vertical", type: String })
	public readonly resize!: "both" | "horizontal" | "none" | "vertical";

	@Prop({ type: Boolean })
	public readonly helpDisabled!: boolean;

	@Ref()
	public readonly editorContainer!: HTMLDivElement;

	@Ref()
	public readonly editorWrapper!: HTMLDivElement;

	@Ref()
	public readonly contextMenu!: HTMLDivElement;

	private activeSuggestionsCancelSource: CancelTokenSource | null = null;
	private contextMenuPopper: Instance | null = null;
	private contextMenuReference: VirtualElement | null = null;
	private editor: FormulaCodeMirrorEditor | null = null;
	private externalTableQueries: ExternalTableQueryDto[] | null = null;
	private externalTableQueriesPromise: Promise<ExternalTableQueryDto[]> | null = null;
	private isContextMenuVisible = false;
	private lastObservedWidthPx: number | null = null;
	private lastUsedFormatMode: FormulaFormatMode = "compact";
	private pendingHeightSyncFrameId: number | null = null;
	private resizeObserver: ResizeObserver | null = null;

	private isTargetInsideContextMenu(target: EventTarget | null): boolean {
		return Boolean(target instanceof Node && this.contextMenu?.contains(target));
	}

	private closeContextMenuIfOutside(target: EventTarget | null): void {
		if (!this.isContextMenuVisible || this.isTargetInsideContextMenu(target)) {
			return;
		}

		this.closeContextMenu();
	}

	private readonly handleDocumentPointerDown = (event: PointerEvent) => {
		this.closeContextMenuIfOutside(event.target);
	};
	private readonly handleDocumentContextMenu = (event: MouseEvent) => {
		if (!this.isContextMenuVisible) {
			return;
		}

		if (this.isTargetInsideContextMenu(event.target)) {
			return;
		}

		this.closeContextMenu();
	};
	private readonly handleWindowBlur = () => {
		this.closeContextMenu();
	};
	private readonly handleDocumentKeyDown = (event: KeyboardEvent) => {
		if ((event.key === "Escape" || event.key === "Esc") && this.isContextMenuVisible) {
			event.preventDefault();
			event.stopPropagation();
			this.closeContextMenu();
		}
	};

	public get helpTooltip() {
		if (this.helpDisabled) {
			return null;
		}

		return [
			this.$t("help.navigation", ["↑", "↓"]),
			this.$t("help.show", ["Ctrl/Cmd", this.$t("key.space")]),
			this.$t("help.indent", ["Tab", "Shift+Tab"]),
			this.$t("help.comment", ["Ctrl+/", "Cmd+:"]),
			this.$t("help.valid", [this.$t("key.enter"), "Tab", this.$t("key.end")]),
			this.$t("help.format", [this.contextMenuFormatShortcut, this.currentFormatModeLabel]),
			this.$t("help.formatMenu")
		].join("\n");
	}

	public get currentFormatModeLabel(): string {
		return this.lastUsedFormatMode === "readable"
			? String(this.$t("formatMode.readable"))
			: String(this.$t("formatMode.compact"));
	}

	public get contextMenuFormatShortcut(): string {
		return "Shift+Alt+F";
	}

	public mounted(): void {
		this.editor = createFormulaCodeMirrorEditor({
			container: this.editorContainer,
			format: formatFormula,
			onChange: value => {
				this.scheduleHeightSync();
				this.$emit("input", value || null);
			},
			placeholder: this.placeholder,
			provideCompletionItems: this.provideCompletionItems.bind(this),
			readOnly: this.disabled || this.sending,
			validate: collectSyntaxErrors,
			value: this.value
		});

		window.addEventListener("blur", this.handleWindowBlur);
		window.addEventListener("resize", this.handleWindowBlur);
		document.addEventListener("keydown", this.handleDocumentKeyDown, true);
		document.addEventListener("pointerdown", this.handleDocumentPointerDown, true);
		document.addEventListener("contextmenu", this.handleDocumentContextMenu, true);
		this.syncLastUsedFormatMode();
		this.startObservingEditorResize();
		this.scheduleHeightSync();

		this.validate();
	}

	private startObservingEditorResize(): void {
		if (typeof ResizeObserver === "undefined") {
			return;
		}

		this.resizeObserver?.disconnect();
		this.resizeObserver = new ResizeObserver(() => {
			const currentWidth = Math.round(this.editorContainer.offsetWidth);
			if (!currentWidth) {
				return;
			}

			if (this.lastObservedWidthPx == null) {
				this.lastObservedWidthPx = currentWidth;
				return;
			}

			if (currentWidth === this.lastObservedWidthPx) {
				return;
			}

			this.lastObservedWidthPx = currentWidth;
			this.scheduleHeightSync();
		});
		this.resizeObserver.observe(this.editorContainer);
	}

	private scheduleHeightSync(): void {
		if (!this.editor) {
			return;
		}

		if (this.pendingHeightSyncFrameId != null) {
			window.cancelAnimationFrame(this.pendingHeightSyncFrameId);
		}

		this.$nextTick(() => {
				this.pendingHeightSyncFrameId = window.requestAnimationFrame(() => {
					this.pendingHeightSyncFrameId = null;

					if (!this.editor) {
						return;
					}

					const targetHeight = this.editor.getContentHeight();
					if (!targetHeight) {
						return;
					}

					if (Math.round(this.editorContainer.offsetHeight) === targetHeight) {
						return;
					}

					this.editorContainer.style.height = `${targetHeight}px`;
				});
		});
	}

	private syncLastUsedFormatMode(): void {
		if (!this.editor) {
			return;
		}

		this.lastUsedFormatMode = this.editor.getLastUsedFormatMode();
	}

	private createContextMenuReference(clientX: number, clientY: number): VirtualElement {
		return {
			contextElement: this.editorWrapper,
			getBoundingClientRect: () => ({
				bottom: clientY,
				height: 0,
				left: clientX,
				right: clientX,
				top: clientY,
				width: 0,
				x: clientX,
				y: clientY,
				toJSON: () => ({} as Record<string, never>)
			})
		};
	}

	private updateContextMenuPopper(): void {
		if (!this.isContextMenuVisible || !this.contextMenu || !this.contextMenuReference) {
			return;
		}

		this.destroyContextMenuPopper();
		this.contextMenuPopper = createPopper(this.contextMenuReference, this.contextMenu, {
			placement: "bottom-start",
			strategy: "fixed",
			modifiers: [
				{
					name: "offset",
					options: {
						offset: [0, 4]
					}
				},
				{
					name: "flip",
					options: {
						fallbackPlacements: ["top-start", "right-start", "left-start"]
					}
				},
				{
					name: "preventOverflow",
					options: {
						altAxis: true,
						padding: 8
					}
				}
			]
		});
		this.contextMenuPopper.forceUpdate();
	}

	private destroyContextMenuPopper(): void {
		this.contextMenuPopper?.destroy();
		this.contextMenuPopper = null;
	}

	private openContextMenu(event: MouseEvent): void {
		this.syncLastUsedFormatMode();
		this.contextMenuReference = this.createContextMenuReference(event.clientX, event.clientY);
		this.isContextMenuVisible = true;
		this.$nextTick(() => {
			this.updateContextMenuPopper();
			this.contextMenu?.focus();
		});
	}

	private closeContextMenu(): void {
		this.destroyContextMenuPopper();
		this.contextMenuReference = null;
		this.isContextMenuVisible = false;
	}

	private formatCompactFromContextMenu(): void {
		this.formatEditor("compact");
	}

	private formatReadableFromContextMenu(): void {
		this.formatEditor("readable");
	}

	private formatEditor(mode?: FormulaFormatMode): void {
		this.editor?.format(mode);
		this.syncLastUsedFormatMode();
		this.closeContextMenu();
	}

	private createRange(lineNumber: number, startColumn: number, endColumn: number): FormulaRange {
		return {
			startLineNumber: lineNumber,
			endLineNumber: lineNumber,
			startColumn,
			endColumn
		};
	}

	private getPositionAtOffset(value: string, offset: number): { column: number; lineNumber: number } {
		const safeOffset = Math.max(0, Math.min(offset, value.length));
		let lineNumber = 1;
		let column = 1;

		for (let index = 0; index < safeOffset; index++) {
			const character = value[index];

			if (character === "\r") {
				if (value[index + 1] === "\n") {
					index++;
				}

				lineNumber++;
				column = 1;
				continue;
			}

			if (character === "\n") {
				lineNumber++;
				column = 1;
				continue;
			}

			column++;
		}

		return { column, lineNumber };
	}

	private createRangeFromOffsets(value: string, startOffset: number, endOffset: number): FormulaRange {
		const startPosition = this.getPositionAtOffset(value, startOffset);
		const endPosition = this.getPositionAtOffset(value, endOffset);

		return {
			startLineNumber: startPosition.lineNumber,
			startColumn: startPosition.column,
			endLineNumber: endPosition.lineNumber,
			endColumn: endPosition.column
		};
	}

	private getLineStartOffset(value: string, offset: number): number {
		let index = Math.max(0, Math.min(offset, value.length)) - 1;

		while (index >= 0) {
			const character = value[index];
			if (character === "\n" || character === "\r") {
				return index + 1;
			}

			index--;
		}

		return 0;
	}

	private getWordUntilOffset(value: string, offset: number): FormulaWord {
		const safeOffset = Math.max(0, Math.min(offset, value.length));
		const lineStartOffset = this.getLineStartOffset(value, safeOffset);
		const textBeforeCaret = value.slice(lineStartOffset, safeOffset);
		const word = textBeforeCaret.match(FormulaEditor.WORD_END_REGEX)?.[0] || "";
		const endColumn = textBeforeCaret.length + 1;

		return {
			endColumn,
			startColumn: Math.max(1, endColumn - word.length),
			word
		};
	}

	private getCompletionContext(value: string, offset: number): CompletionContext {
		const textBeforeCaret = value.slice(0, offset);
		const completionContext = resolveCompletionContext(textBeforeCaret);

		if (completionContext.kind === "table") {
			return {
				kind: "table",
				tableContext: completionContext.tableContext,
				currentArgRaw: completionContext.currentArgRaw,
				currentArgValue: unquote(completionContext.currentArgRaw),
				range: this.createRangeFromOffsets(value, completionContext.rangeStartOffset, completionContext.rangeEndOffset)
			};
		}

		if (completionContext.kind === "function") {
			return {
				kind: "function",
				functionContext: completionContext.functionContext,
				currentArgRaw: completionContext.currentArgRaw,
				currentArgValue: unquote(completionContext.currentArgRaw),
				range: this.createRangeFromOffsets(value, completionContext.rangeStartOffset, completionContext.rangeEndOffset),
				word: this.getWordUntilOffset(value, offset)
			};
		}

		return {
			kind: "variable",
			variable: completionContext.variable,
			word: this.getWordUntilOffset(value, offset)
		};
	}

	private toCompletionOptions(
		suggestions: Suggestion[],
		lineNumber: number,
		column: number,
		word: FormulaWord,
		variable: Variable
	): FormulaCompletionOption[] {
		return this.sortSuggestionsByMatch(suggestions, variable.code).map((suggestion, index) => {
			let range = this.createRange(lineNumber, word.startColumn, word.endColumn);
			const filterPrefix = variable.code || suggestion.code;

			if (suggestion.append) {
				if (variable.code.endsWith(VARIABLE_PARTS_SEPARATOR)) {
					range = this.createRange(lineNumber, column, column);
				} else {
					const codeParts = variable.code.split(VARIABLE_PARTS_SEPARATOR);
					const lastPart = codeParts[codeParts.length - 1] ?? "";
					const startColumn = Math.max(1, column - lastPart.length);
					range = this.createRange(lineNumber, startColumn, column);
				}
			}

			return this.createCompletionOption(suggestion, range, filterPrefix, index);
		});
	}

	private createCompletionOption(
		suggestion: Suggestion,
		range: FormulaRange,
		filterPrefix: string,
		index: number
	): FormulaCompletionOption {
		let insertText = suggestion.insertText || suggestion.code;

		if (suggestion.appendSeparator && !suggestion.last) {
			insertText += VARIABLE_PARTS_SEPARATOR;
		}

		return {
			label: suggestion.code,
			kind: suggestion.kind,
			detail: suggestion.label,
			filterText: `${filterPrefix} ${suggestion.code}${suggestion.label ? ` ${suggestion.label}` : ""}`,
			insertText,
			range,
			section: suggestion.section,
			sortText: index.toString().padStart(4, "0")
		};
	}

	private toContextualCompletionOptions(
		suggestions: Suggestion[],
		range: FormulaRange,
		filterPrefix: string
	): FormulaCompletionOption[] {
		return this.sortSuggestionsByMatch(suggestions, filterPrefix).map((suggestion, index) => {
			return this.createCompletionOption(suggestion, range, filterPrefix || suggestion.code, index);
		});
	}

	private getSuggestionMatchRank(input: string, suggestion: Suggestion): number {
		const normalizedInput = input.trim().toLowerCase();
		if (!normalizedInput) {
			return 0;
		}

		const normalizedCode = suggestion.code.toLowerCase();
		if (normalizedCode.startsWith(normalizedInput)) {
			return 0;
		}

		if (normalizedCode.includes(normalizedInput)) {
			return 1;
		}

		return 2;
	}

	private getSuggestionSectionRank(suggestion: Suggestion): number {
		if (!suggestion.section) {
			return 99;
		}

		return FormulaEditor.COMPLETION_SECTION_RANKS[suggestion.section] ?? 99;
	}

	private sortSuggestionsByMatch(suggestions: Suggestion[], input: string): Suggestion[] {
		const normalizedInput = input.trim().toLowerCase();
		if (!normalizedInput) {
			return suggestions;
		}

		return [...suggestions].sort((leftSuggestion, rightSuggestion) => {
			const matchRankDifference =
				this.getSuggestionMatchRank(normalizedInput, leftSuggestion) -
				this.getSuggestionMatchRank(normalizedInput, rightSuggestion);
			if (matchRankDifference !== 0) {
				return matchRankDifference;
			}

			const sectionRankDifference =
				this.getSuggestionSectionRank(leftSuggestion) - this.getSuggestionSectionRank(rightSuggestion);
			if (sectionRankDifference !== 0) {
				return sectionRankDifference;
			}

			return leftSuggestion.code.localeCompare(rightSuggestion.code, "fr", { sensitivity: "base" });
		});
	}

	private async loadSimpleVariableSuggestions<T extends { code: string; label?: string }>(
		loader: (cancelToken: CancelToken) => Promise<{ data: T[] }>,
		cancelToken: CancelToken
	): Promise<Suggestion[]> {
		return toVariableSuggestions((await loader(cancelToken)).data);
	}

	private async loadAdditionalDataSuggestions(
		code: string,
		limit: number,
		cancelToken: CancelToken
	): Promise<Suggestion[]> {
		return this.loadSimpleVariableSuggestions(
			token => AdditionalDataApi.list(null, null, limit, code, null, null, { cancelToken: token }),
			cancelToken
		);
	}

	private async loadEmployeeSuggestions(code: string, limit: number, cancelToken: CancelToken): Promise<Suggestion[]> {
		return this.loadSimpleVariableSuggestions(
			token => EmployeesDetailsApi.list(null, null, limit, code, null, null, { cancelToken: token }),
			cancelToken
		);
	}

	private async loadInstitutionSuggestions(
		code: string,
		limit: number,
		cancelToken: CancelToken
	): Promise<Suggestion[]> {
		return this.loadSimpleVariableSuggestions(
			token => InstitutionDetailsApi.list(null, null, limit, code, null, null, { cancelToken: token }),
			cancelToken
		);
	}

	private async loadLigneSuggestions(
		variable: Variable,
		limit: number,
		cancelToken: CancelToken
	): Promise<Suggestion[]> {
		const filters = ["code,EXIST,TRUE"];
		const suggestions = toVariableSuggestions(
			(
				await ReferentialPayApi.list(null, null, limit, variable.code, null, filters, { cancelToken })
			).data.filter(referentialPay => referentialPay.dateArchived == null)
		);

		if (suggestions.length) {
			return suggestions;
		}

		return getLigneColumnSuggestions(variable);
	}

	private async fetchSuggestions(variable: Variable, cancelToken: CancelToken): Promise<Suggestion[]> {
		const request = getVariableSuggestionsRequest(this.editor?.getValue() || "", variable);
		if (request.kind === "initial") {
			return request.suggestions || [];
		}

		const limit = 5;
		const loaders: Record<VariableType, () => Promise<Suggestion[]>> = {
			donneeAnnexe: () => this.loadAdditionalDataSuggestions(request.query || "", limit, cancelToken),
			employee: () => this.loadEmployeeSuggestions(request.query || "", limit, cancelToken),
			institution: () => this.loadInstitutionSuggestions(request.query || "", limit, cancelToken),
			ligne: () => this.loadLigneSuggestions(variable, limit, cancelToken)
		};

		return (await loaders[request.source as VariableType]?.()) || [];
	}

	private async resolveVariableSuggestions(
		context: VariableCompletionContext,
		lineNumber: number,
		column: number,
		cancelToken: CancelToken
	): Promise<FormulaCompletionOption[]> {
		const suggestions = await this.fetchSuggestions(context.variable, cancelToken);
		return this.toCompletionOptions(suggestions, lineNumber, column, context.word, context.variable);
	}

	private async resolveFunctionSuggestions(
		context: FunctionCompletionContext,
		lineNumber: number,
		column: number,
		cancelToken: CancelToken
	): Promise<FormulaCompletionOption[]> {
		const functionSuggestions = getFunctionArgumentSuggestions(
			context.functionContext.functionName,
			context.functionContext.currentArgIndex,
			context.currentArgValue
		);

		if (functionSuggestions.length) {
			return this.toContextualCompletionOptions(functionSuggestions, context.range, context.currentArgValue);
		}

		const variable = extractVariable(context.currentArgRaw);
		if (!variable.prefix && !variable.code) {
			return [];
		}

		if (!variable.prefix) {
			return this.toContextualCompletionOptions(
				getPrefixSuggestions(variable.code),
				context.range,
				variable.code
			);
		}

		return this.resolveVariableSuggestions(
			{
				kind: "variable",
				variable,
				word: context.word
			},
			lineNumber,
			column,
			cancelToken
		);
	}

	private async getExternalTableQueries(): Promise<ExternalTableQueryDto[]> {
		if (this.externalTableQueries) {
			return this.externalTableQueries;
		}

		if (!this.externalTableQueriesPromise) {
			this.externalTableQueriesPromise = ExternalTableQueryApi.listAll({}, "none")
				.then(queries => {
					this.externalTableQueries = queries || [];
					return this.externalTableQueries;
				})
				.catch(() => {
					return [];
				})
				.finally(() => {
					this.externalTableQueriesPromise = null;
				});
		}

		return this.externalTableQueriesPromise;
	}

	private buildTableQuerySuggestions(
		queries: ExternalTableQueryDto[],
		currentArgValue: string,
		range: FormulaRange
	): FormulaCompletionOption[] {
		return queries
			.filter(query => (query.code || "").toLowerCase().includes(currentArgValue.toLowerCase()))
			.sort((leftQuery, rightQuery) => {
				const leftCode = leftQuery.code || "";
				const rightCode = rightQuery.code || "";
				const normalizedInput = currentArgValue.trim().toLowerCase();

				const leftStartsWith = normalizedInput ? leftCode.toLowerCase().startsWith(normalizedInput) : false;
				const rightStartsWith = normalizedInput ? rightCode.toLowerCase().startsWith(normalizedInput) : false;
				if (leftStartsWith !== rightStartsWith) {
					return leftStartsWith ? -1 : 1;
				}

				return leftCode.localeCompare(rightCode, "fr", { sensitivity: "base" });
			})
			.map((query, index) => {
				const code = query.code || "";
				const item = this.createCompletionOption(
					{
						code,
						label: query.label || query.description || "Table externe",
						kind: "variable",
						section: "Tables externes"
					},
					range,
					currentArgValue || code,
					index
				);
				return { ...item, insertText: quote(code) };
			});
	}

	private buildTableExpectedArgSuggestion(
		expectedArg: ExternalTableExpectedArg,
		currentArgValue: string,
		range: FormulaRange
	): FormulaCompletionOption[] {
		return [
			this.createCompletionOption(
				{
					code: expectedArg.insertText,
					label: expectedArg.detail,
					kind: "value",
					section: "Valeurs attendues"
				},
				range,
				currentArgValue || expectedArg.insertText,
				0
			)
		];
	}

	private resolveTableQuerySuggestions(
		context: TableCompletionContext,
		queries: ExternalTableQueryDto[]
	): FormulaCompletionOption[] {
		return this.buildTableQuerySuggestions(queries, context.currentArgValue, context.range);
	}

	private resolveTableParameterSuggestions(
		context: TableCompletionContext,
		queries: ExternalTableQueryDto[]
	): FormulaCompletionOption[] {
		const expectedArg = getExpectedTableArg(context, queries);
		if (!expectedArg) {
			return [];
		}

		return this.buildTableExpectedArgSuggestion(expectedArg, context.currentArgValue, context.range);
	}

	private async resolveTableSuggestions(
		context: TableCompletionContext
	): Promise<FormulaCompletionOption[] | null> {
		const queries = await this.getExternalTableQueries();
		const resolver =
			context.tableContext.currentArgIndex === 0
				? this.resolveTableQuerySuggestions.bind(this)
				: this.resolveTableParameterSuggestions.bind(this);

		return resolver(context, queries);
	}

	private async resolveSuggestions(
		context: CompletionContext,
		lineNumber: number,
		column: number,
		cancelToken: CancelToken
	): Promise<FormulaCompletionOption[]> {
		const resolvers: Record<CompletionContext["kind"], () => Promise<FormulaCompletionOption[]>> = {
			function: () =>
				this.resolveFunctionSuggestions(context as FunctionCompletionContext, lineNumber, column, cancelToken),
			table: async () => (await this.resolveTableSuggestions(context as TableCompletionContext)) || [],
			variable: () =>
				this.resolveVariableSuggestions(context as VariableCompletionContext, lineNumber, column, cancelToken)
		};

		return resolvers[context.kind]();
	}

	private async provideCompletionItems(
		value: string,
		offset: number,
		explicit: boolean
	): Promise<FormulaCompletionOption[]> {
		this.activeSuggestionsCancelSource?.cancel("Superseded formula completion request.");
		const cancelTokenSource = axios.CancelToken.source();
		this.activeSuggestionsCancelSource = cancelTokenSource;

		const { column, lineNumber } = this.getPositionAtOffset(value, offset);
		const completionContext = this.getCompletionContext(value, offset);
		try {
			const suggestions = await this.resolveSuggestions(
				completionContext,
				lineNumber,
				column,
				cancelTokenSource.token
			);

			if (!explicit && !suggestions.length) {
				return [];
			}

			if (this.activeSuggestionsCancelSource !== cancelTokenSource) {
				return [];
			}

			return suggestions;
		} catch (error) {
			if (axios.isCancel(error)) {
				return [];
			}

			if (completionContext.kind === "table") {
				console.error("Error fetching external table suggestions", error);
			} else {
				console.error("Error fetching suggestions", error);
			}

			return [];
		} finally {
			if (this.activeSuggestionsCancelSource === cancelTokenSource) {
				this.activeSuggestionsCancelSource = null;
			}
		}
	}

	private validate(): void {
		this.editor?.validate();
	}

	public beforeDestroy(): void {
		window.removeEventListener("blur", this.handleWindowBlur);
		window.removeEventListener("resize", this.handleWindowBlur);
		document.removeEventListener("keydown", this.handleDocumentKeyDown, true);
		document.removeEventListener("pointerdown", this.handleDocumentPointerDown, true);
		document.removeEventListener("contextmenu", this.handleDocumentContextMenu, true);
		this.lastObservedWidthPx = null;
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;

		if (this.pendingHeightSyncFrameId != null) {
			window.cancelAnimationFrame(this.pendingHeightSyncFrameId);
			this.pendingHeightSyncFrameId = null;
		}

		this.activeSuggestionsCancelSource?.cancel("Formula editor destroyed.");
		this.activeSuggestionsCancelSource = null;
		this.destroyContextMenuPopper();
		this.contextMenuReference = null;
		this.editor?.destroy();
		this.editor = null;
	}

	@Watch("value")
	private onValueChanged(newValue?: string | null): void {
		if (this.editor?.getValue() !== newValue) {
			this.editor?.setValue(newValue ?? "");
		}

		this.scheduleHeightSync();
	}

	@Watch("disabled")
	private onDisabledChanged(disabled: boolean): void {
		this.editor?.setReadOnly(disabled || this.sending);
	}

	@Watch("sending")
	private onSendingChanged(sending: boolean): void {
		this.editor?.setReadOnly(this.disabled || sending);
	}
}
</script>

<i18n>
{
	"fr": {
		"contextMenu.format": "Mettre en forme avec le dernier mode ({0})",
		"contextMenu.formatCompact": "Mettre en forme en mode compact",
		"contextMenu.formatReadable": "Mettre en forme en mode lisible",
		"formatMode.compact": "mode compact",
		"formatMode.readable": "mode lisible",
		"help.comment": "Utilisez {0} sur PC ou {1} sur Mac pour activer ou désactiver les commentaires des lignes sélectionnées.",
		"help.format": "Utilisez {0} pour remettre en forme en {1}.",
		"help.formatMenu": "Utilisez le menu contextuel pour choisir explicitement le mode compact ou lisible.",
		"help.indent": "Utilisez {0} pour augmenter et {1} pour diminuer l'indentation des lignes sélectionnées.",
		"help.navigation": "Utilisez les touches {0} ou {1} de votre clavier pour naviguer dans les suggestions.",
		"help.show": "Utilisez les touches {0} + {1} de votre clavier pour afficher les suggestions.",
		"help.valid": "Utilisez les touches {0}, {1}, {2} ou {3} de votre clavier pour valider la suggestion.",
		"key.end": "Fin",
		"key.enter": "Entrée",
		"key.space": "Espace"
	}
}
</i18n>
